import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskSections } from './hooks/useTaskSections';
import TaskSection from './components/TaskSection';
import TaskEmptyState from './components/TaskEmptyState';
import Page from '../../../../components/Page';
import { useToggleTaskComplete } from '../../hooks/useRecordMutations';
import useRecordDetail from '../../hooks/useRecordDetail';
import useRecordList from '../../hooks/useRecordList';
import useFilterStore from '../../stores/useFilterStore';
import { useToast } from '../../../../hooks/useToast';
import { useRecordsLayoutContext } from '../../hooks/useRecordsLayoutContext';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';
import styles from './TaskView.module.css';

const TaskView = () => {
  const { t } = useTranslation();
  const { searchQuery } = useRecordsLayoutContext();
  const toast = useToast();
  const toggleTaskComplete = useToggleTaskComplete();
  const { openDetail } = useRecordDetail();
  const scope = useFilterStore((state) => state.scope);

  // Use Edge-Indexed list hook
  const { records, loading, error } = useRecordList({
    viewType: 'task',
    scope,
    sortSpec: { key: 'taskOrder', dir: 'asc' }, // Edge tier ensures grouped sections
    searchTerm: searchQuery,
    pageSize: 50
  });

  // Records are loaded via Edge-Indexed architecture with view-scoped subscription

  // Organize records into sections
  const sections = useTaskSections(records);

  // When search is active, records are already server-filtered and ranked by Algolia.
  // Build sections from the returned (already filtered) records - no client-side re-filtering.
  const filteredSections = sections;
  const errorKey = error
    ? mapRecordsErrorToKey(error, { fallbackKey: 'records.views.task.errors.load_failed' })
    : null;

  // Handle task click - open record detail view
  const handleTaskClick = (task) => {
    openDetail(task);
  };

  // Handle task completion
  const handleTaskComplete = useCallback(async (taskId, isComplete) => {
    // Use mutation hook to toggle task completion
    toggleTaskComplete.mutate(
      {
        recordId: taskId,
        isCompleted: isComplete
      },
      {
        onError: (error) => {
          console.error('Error updating task completion:', error);
          toast.error(
            t(
              mapRecordsErrorToKey(error, {
                fallbackKey: 'records.views.task.errors.update_failed'
              })
            )
          );
        }
      }
    );
    // No need to refetch - mutation hook handles cache invalidation automatically
  }, [t, toggleTaskComplete, toast]);

  // Loading state
  if (loading && records.length === 0) {
    return (
      <div className={styles.taskView}>
        <div className={styles.loadingContainer}>
          {/* Loading skeletons */}
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.taskSkeleton}>
              <div className={styles.skeletonCheckbox} />
              <div className={styles.skeletonContent}>
                <div className={styles.skeletonTitle} />
                <div className={styles.skeletonMeta} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.taskView}>
        <div className={styles.errorContainer}>
          <p>{errorKey ? t(errorKey) : t('records.views.task.errors.load_failed')}</p>
        </div>
      </div>
    );
  }

  // Check if all active sections are empty
  const hasActiveTasks = 
    filteredSections.overdue.length > 0 ||
    filteredSections.today.length > 0 ||
    filteredSections.upcoming.length > 0 ||
    filteredSections.noDate.length > 0;

  return (
    <Page width="default">
      <div className={styles.taskView}>
      {/* Active Tasks Container */}
      <div className={styles.activeTasksContainer}>
        {/* Overdue Section */}
        <TaskSection
          id="overdue"
          title={t('records.views.task.sections.overdue')}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          }
          color="var(--semantic-error)"
          tasks={filteredSections.overdue}
          onTaskClick={handleTaskClick}
          onTaskComplete={handleTaskComplete}
        />

        {/* Today Section */}
        <TaskSection
          id="today"
          title={t('records.views.task.sections.today')}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="1" />
            </svg>
          }
          color="var(--accent-primary)"
          tasks={filteredSections.today}
          onTaskClick={handleTaskClick}
          onTaskComplete={handleTaskComplete}
        />

        {/* Upcoming Section */}
        <TaskSection
          id="upcoming"
          title={t('records.views.task.sections.upcoming')}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          }
          color="var(--text-secondary)"
          tasks={filteredSections.upcoming}
          onTaskClick={handleTaskClick}
          onTaskComplete={handleTaskComplete}
        />

        {/* No Date Section */}
        <TaskSection
          id="no-date"
          title={t('records.views.task.sections.no_date')}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          }
          tasks={filteredSections.noDate}
          onTaskClick={handleTaskClick}
          onTaskComplete={handleTaskComplete}
        />
      </div>

      {/* Empty State */}
      {!hasActiveTasks && <TaskEmptyState />}

      {/* Completed Tasks Section */}
      <TaskSection
        id="completed"
        title={t('records.views.task.sections.completed')}
        color="var(--semantic-success)"
        tasks={filteredSections.completed}
        count={filteredSections.completed.length}
        isCollapsible
        defaultCollapsed
        onTaskClick={handleTaskClick}
        onTaskComplete={handleTaskComplete}
      />

      </div>
    </Page>
  );
};

export default TaskView;
